import * as React from "react";

import { cleanedErrors } from "@/utils/Functions";
import { RadioGroupInput } from "@/components/atoms";
import { FormField } from "@/components/molecules/FormField";
import { FormStack } from "@/components/molecules/FormStack";
import { useUpdater } from "@/utils/callbacks";
import {
  PersonDetailsFields,
  errorsOf as errorsOfPersonDetails,
} from "./PersonDetailsFields";
import { defaultResponsible, fieldsFromPersons } from "./responsible";
import { Person } from "@/types";
import { NestedErrors } from "@/utils/Types";

type NewAccountHolder = {
  readonly responsibleId?: string;
  readonly responsible?: Person;
};

type NewAccountHolderErrors = NestedErrors<NewAccountHolder> | undefined;


export const errorsOfNewAccountHolder: (r?: NewAccountHolder) => NewAccountHolderErrors = (record) => {
  return cleanedErrors({
    responsibleId: record?.responsibleId || record?.responsibleId === "" ? undefined : "required",
    responsible:
      record?.responsibleId !== "" ? undefined : errorsOfPersonDetails(record?.responsible),
  });
};

export type NewAccountHolderFieldsProps = {
  readonly serviceProvider: { customForm?: string };
  readonly persons: ReadonlyArray<Person>;
  readonly labelOverride?: string;
  readonly record: NewAccountHolder;
  readonly errors: NewAccountHolderErrors;
  readonly update: any;
};

export const NewAccountHolderFields: React.FC<NewAccountHolderFieldsProps> = ({
  serviceProvider,
  persons,
  labelOverride = "Who will take over the account?",
  record,
  errors,
  update,
}) => {
  const onNewAccountHolderIdChange = React.useCallback(
    (responsibleId) => {
      if (responsibleId) {
        update({ responsibleId });
      } else if (responsibleId === "") {
        update({ responsibleId, responsible: {} });
      } else {
        update({});
      }
    },
    [update]
  );

  const updateNewAccountHolder = useUpdater(update, "responsible");

  React.useEffect(() => {
    if (typeof record.responsibleId === "string") {
      return;
    }

    const person = defaultResponsible(persons);
    if (person) {
      onNewAccountHolderIdChange(person.id);
    }
  }, [record, persons, onNewAccountHolderIdChange]);

  return (
    <FormStack substack spacing={0}>
      <FormField label={labelOverride}>
        <RadioGroupInput
          onValueChange={onNewAccountHolderIdChange}
          value={record.responsibleId}
          fields={fieldsFromPersons(persons, true)}
          error={errors?.responsibleId}
        />
      </FormField>

      {record.responsible && (
        <FormStack indented substack>
          <PersonDetailsFields
            serviceProvider={serviceProvider}
            record={record.responsible}
            update={updateNewAccountHolder}
            errors={errors?.responsible}
          />
        </FormStack>
      )}
    </FormStack>
  );
};
